import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientSelector = ({ patients, selectedPatient, onSelectPatient, onPatientsChange }) => {
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [patientForm, setPatientForm] = useState({
    name: '',
    relationship: '',
    date_of_birth: '',
    gender: '',
    health_card_number: '',
    ailment: '',
    cancer_type: '',
    doctors: '',
  });

  const handleAddPatient = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      const doctorsList = patientForm.doctors ? patientForm.doctors.split(',').map(d => d.trim()).filter(d => d) : [];
      
      // Calculate age from date of birth
      let age = null;
      if (patientForm.date_of_birth) {
        const birthDate = new Date(patientForm.date_of_birth);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }
      
      const response = await axios.post(
        `${API}/patients`,
        {
          name: patientForm.name,
          relationship: patientForm.relationship,
          date_of_birth: patientForm.date_of_birth,
          age: age,
          gender: patientForm.gender,
          health_card_number: patientForm.health_card_number,
          ailment: patientForm.ailment,
          cancer_type: patientForm.cancer_type,
          doctors: doctorsList,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient added successfully');
      setShowAddPatient(false);
      setPatientForm({ name: '', relationship: '', date_of_birth: '', gender: '', health_card_number: '', ailment: '', cancer_type: '', doctors: '' });
      onPatientsChange();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Free plan allows only 1 patient. Upgrade to premium for unlimited patients.');
      } else {
        toast.error('Failed to add patient');
      }
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="patient-selector">
      <Select
        value={selectedPatient?.id}
        onValueChange={(value) => {
          const patient = patients.find((p) => p.id === value);
          onSelectPatient(patient);
        }}
      >
        <SelectTrigger className="w-[200px]" data-testid="select-patient-trigger">
          <SelectValue placeholder="Select patient">
            <div className="flex items-center gap-2">
              <Users size={16} />
              {selectedPatient?.is_shared && '👥 '}
              {selectedPatient?.name}
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {patients.map((patient) => (
            <SelectItem key={patient.id} value={patient.id} data-testid={`patient-${patient.id}`}>
              <div className="flex items-center gap-2">
                {patient.is_shared && <span className="text-xs">👥</span>}
                {patient.name} ({patient.relationship})
                {patient.is_shared && <span className="text-xs text-gray-500">(Shared)</span>}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={showAddPatient} onOpenChange={setShowAddPatient}>
        <DialogTrigger asChild>
          <button
            data-testid="add-patient-button"
            className="btn btn-primary flex items-center gap-2 p-2 px-3"
            style={{ borderRadius: '50px' }}
          >
            <Plus size={16} />
          </button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Patient</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPatient} className="space-y-4" data-testid="add-patient-form">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                data-testid="patient-name-input"
                type="text"
                value={patientForm.name}
                onChange={(e) => setPatientForm({ ...patientForm, name: e.target.value })}
                required
                placeholder="Patient's name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Relationship</label>
              <input
                data-testid="patient-relationship-input"
                type="text"
                value={patientForm.relationship}
                onChange={(e) => setPatientForm({ ...patientForm, relationship: e.target.value })}
                required
                placeholder="e.g., Mother, Father"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date of Birth</label>
              <input
                data-testid="patient-dob-input"
                type="date"
                value={patientForm.date_of_birth}
                onChange={(e) => setPatientForm({ ...patientForm, date_of_birth: e.target.value })}
                placeholder="Date of Birth"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Gender</label>
              <select
                data-testid="patient-gender-input"
                value={patientForm.gender}
                onChange={(e) => setPatientForm({ ...patientForm, gender: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Health Card / Insurance Number</label>
              <input
                data-testid="patient-health-card-input"
                type="text"
                value={patientForm.health_card_number}
                onChange={(e) => setPatientForm({ ...patientForm, health_card_number: e.target.value })}
                placeholder="e.g., 1234-567-890"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Ailment/Condition</label>
              <input
                data-testid="patient-ailment-input"
                type="text"
                value={patientForm.ailment}
                onChange={(e) => setPatientForm({ ...patientForm, ailment: e.target.value })}
                placeholder="e.g., Cancer, Lymphoma"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Cancer Type (if applicable)</label>
              <input
                data-testid="patient-cancer-type-input"
                type="text"
                value={patientForm.cancer_type}
                onChange={(e) => setPatientForm({ ...patientForm, cancer_type: e.target.value })}
                placeholder="e.g., Non-Hodgkin Lymphoma, Stage II"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Doctors (comma-separated)</label>
              <input
                data-testid="patient-doctors-input"
                type="text"
                value={patientForm.doctors}
                onChange={(e) => setPatientForm({ ...patientForm, doctors: e.target.value })}
                placeholder="e.g., Dr. Smith, Dr. Johnson"
              />
            </div>
            <button data-testid="submit-patient-button" type="submit" className="btn btn-primary w-full">
              Add Patient
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientSelector;
